import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  ArrowsLeftRight, Bell, Briefcase, Calculator, ChartPie, CheckSquare, ClipboardText, FileText,
  GearSix, Lifebuoy, List, LockSimple, Money, Moon, SignOut, SidebarSimple, SquaresFour, Sun, Target, Ticket, TrendUp,
  User, Users,
} from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import BrandLogo from "./BrandLogo";
import LiveClock from "./LiveClock";
import AccountSettingsDialog from "./AccountSettingsDialog";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { fmtDateTime } from "../lib/format";

const NAV = {
  investor: [
    { to: "/dashboard", label: "Overview", icon: SquaresFour },
    { to: "/portfolio", label: "Portfolio", icon: Briefcase },
    { to: "/performance", label: "Performance", icon: TrendUp },
    { to: "/holdings", label: "Holdings", icon: ChartPie },
    { to: "/transactions", label: "Transactions", icon: ArrowsLeftRight },
    { to: "/documents", label: "Documents", icon: FileText },
    { to: "/investment-risk", label: "Investment Risk", icon: Target },
    { to: "/requests", label: "Requests", icon: Ticket },
    { to: "/calculators", label: "Financial Calculators", icon: Calculator },
    { to: "/profile", label: "Profile", icon: User },
    { to: "/support", label: "Support", icon: Lifebuoy },
  ],
  admin: [
    { to: "/admin", label: "Command Center", icon: SquaresFour },
    { to: "/admin/clients", label: "Clients", icon: Users },
    { to: "/admin/wrms", label: "WRMs", icon: Briefcase },
    { to: "/admin/requests", label: "Requests", icon: Ticket },
    { to: "/admin/transactions", label: "Transactions", icon: ArrowsLeftRight },
    { to: "/admin/kyc", label: "KYC", icon: CheckSquare },
    { to: "/admin/audit", label: "Audit Logs", icon: ClipboardText },
    { to: "/calculators", label: "Financial Calculators", icon: Calculator },
    { to: "/admin/settings", label: "Settings", icon: GearSix },
  ],
  wrm: [
    { to: "/wrm", label: "Overview", icon: SquaresFour },
    { to: "/wrm/clients", label: "My Clients", icon: Users },
    { to: "/wrm/requests", label: "Requests", icon: Ticket },
    { to: "/wrm/pay", label: "My Pay", icon: Money },
    { to: "/calculators", label: "Financial Calculators", icon: Calculator },
  ],
};

const Brand = ({ collapsed }) => (
  <div className={`flex items-center ${collapsed ? "justify-center" : ""}`}>
    {collapsed ? (
      <div className="flex flex-col items-center gap-1" data-testid="sidebar-collapsed-brand" title="KMC Solutions">
        <span className="text-[9px] font-bold tracking-[0.18em] text-emerald-400">DASHBOARD</span>
        <span className="h-[2px] w-6 bg-emerald-500/70 rounded-full" />
      </div>
    ) : (
      <BrandLogo className="h-11 w-auto" testId="sidebar-logo" />
    )}
  </div>
);

const NavItems = ({ items, onNavigate, collapsed }) => (
  <nav className="flex flex-col gap-0.5 px-3">
    {items.map(({ to, label, icon: Icon }) => (
      <NavLink
        key={to}
        to={to}
        end={["/admin", "/wrm", "/dashboard"].includes(to)}
        onClick={onNavigate}
        data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
        title={collapsed ? label : undefined}
        className={({ isActive }) =>
          `relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] font-medium transition-colors duration-200 ${
            isActive
              ? "text-white bg-white/[0.05] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-5 before:w-[2px] before:rounded-full before:bg-emerald-400"
              : "text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.03]"
          } ${collapsed ? "justify-center px-0" : ""}`
        }
      >
        <Icon size={18} weight="duotone" className={collapsed ? "" : "shrink-0"} />
        {!collapsed && label}
      </NavLink>
    ))}
  </nav>
);

function NotificationsBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const load = async () => {
    try {
      const r = await api.get("/notifications");
      setItems(r.data);
    } catch (e) {}
  };
  const unread = items.filter((n) => !n.read).length;
  const openItem = async (n) => {
    if (!n.read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (e) {}
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button data-testid="notifications-bell" className="relative h-9 w-9 rounded-lg border border-white/10 bg-white/[0.02] flex items-center justify-center text-zinc-400 hover:text-white hover:border-emerald-500/30 transition-colors duration-200">
          <Bell size={17} />
          {unread > 0 && (
            <span data-testid="notifications-unread-count" className="absolute -top-1.5 -right-1.5 min-w-[18px] px-1 rounded-full bg-emerald-500 text-[#030706] text-[10px] font-bold flex items-center justify-center h-[18px]">
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0 border-white/10 bg-[#0A110D]/95 backdrop-blur-xl" data-testid="notifications-popover">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
          <p className="font-heading font-semibold text-sm text-white">Notifications</p>
          <button
            data-testid="notifications-mark-read"
            className="text-xs text-emerald-400 font-medium hover:text-emerald-300 transition-colors duration-200"
            onClick={async () => { await api.post("/notifications/read-all"); load(); }}
          >
            Mark all read
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && <p className="text-sm text-zinc-500 p-4">No notifications yet.</p>}
          {items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-white/[0.04] hover:bg-white/[0.03] transition-colors duration-150 ${n.read ? "opacity-50" : ""}`}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 mt-1.5 shrink-0" />}
                <div className={n.read ? "pl-3.5" : ""}>
                  <p className="text-sm font-medium text-white">{n.title}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">{n.message}</p>
                  <p className="text-[10px] text-zinc-600 mt-1">{fmtDateTime(n.created_at)}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function HeaderAvatar({ user }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    setSrc(null);
    if (user?.has_photo) {
      api.get("/auth/photo", { responseType: "blob" })
        .then((r) => { url = window.URL.createObjectURL(r.data); setSrc(url); })
        .catch(() => setSrc(null));
    }
    return () => { if (url) window.URL.revokeObjectURL(url); };
  }, [user?.id, user?.has_photo]);
  const initials = (user?.name || "U").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  if (src) {
    return <img src={src} alt={user?.name} data-testid="header-avatar-photo"
      className="h-7 w-7 rounded-md object-cover border border-emerald-500/30" />;
  }
  return (
    <span className="h-7 w-7 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs font-bold flex items-center justify-center">
      {initials}
    </span>
  );
}

export default function AppLayout({ children, title, subtitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [theme, setTheme] = useState(() => localStorage.getItem("kmc_theme") || "dark");
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem("kmc_theme", theme);
    return () => { document.documentElement.dataset.theme = "dark"; };
  }, [theme]);
  const items = NAV[user?.role] || [];

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#030706] text-white">
      <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-emerald-500/[0.05] blur-[120px]" />
        <div className="absolute top-1/3 right-0 h-[400px] w-[400px] rounded-full bg-emerald-900/[0.08] blur-[100px]" />
      </div>

      <aside className={`hidden lg:flex fixed inset-y-0 left-0 flex-col border-r border-white/[0.06] bg-[#050A08]/90 backdrop-blur-2xl z-40 transition-[width] duration-300 ${collapsed ? "w-[72px]" : "w-60"}`}>
        <div className={`h-16 flex items-center border-b border-white/[0.06] ${collapsed ? "justify-center px-0" : "px-4"}`}>
          <Brand collapsed={collapsed} />
        </div>
        <div className="flex-1 overflow-y-auto py-5">
          <NavItems items={items} collapsed={collapsed} />
        </div>
        <div className="p-3 border-t border-white/[0.06] space-y-2">
          <button
            data-testid="sidebar-collapse-toggle"
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center gap-2 rounded-lg py-2 text-xs text-zinc-500 hover:text-white hover:bg-white/[0.04] transition-colors duration-200"
          >
            <SidebarSimple size={16} />
            {!collapsed && "Collapse"}
          </button>
          {!collapsed && (
            <p className="text-[9px] uppercase tracking-[0.18em] text-zinc-600 font-semibold text-center pb-1">
              © 2026 KMC Solutions
            </p>
          )}
        </div>
      </aside>

      <div className={`relative z-10 flex flex-col min-h-screen transition-[padding] duration-300 ${collapsed ? "lg:pl-[72px]" : "lg:pl-60"}`}>
        <header className="sticky top-0 z-30 h-16 border-b border-white/[0.06] bg-[#030706]/80 backdrop-blur-xl flex items-center gap-3 px-4 md:px-8">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-button" className="lg:hidden h-9 w-9 rounded-lg border border-white/10 bg-white/[0.02] flex items-center justify-center text-zinc-400">
                <List size={18} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 border-white/10 bg-[#050A08]/95 backdrop-blur-2xl">
              <div className="h-16 flex items-center px-4 border-b border-white/[0.06]">
                <Brand />
              </div>
              <div className="py-5">
                <NavItems items={items} onNavigate={() => setMobileOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>

          <div className="hidden md:block">
            {title && <h1 className="font-display font-semibold text-xl tracking-wide text-white" data-testid="page-title">{title}</h1>}
          </div>
          <div className="md:hidden">
            <BrandLogo className="h-10 w-auto" testId="mobile-logo" />
          </div>

          <div className="ml-auto flex items-center gap-2 md:gap-3">
            <div className="hidden sm:block rounded-full border border-white/10 bg-white/[0.02] px-3 py-1.5">
              <LiveClock />
            </div>
            <span data-testid="secure-session-indicator" className="hidden md:flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-zinc-600 font-semibold">
              <LockSimple size={12} className="text-emerald-500/70" /> Secure Session
            </span>
            <button data-testid="theme-toggle-btn" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              title={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
              className="h-9 w-9 rounded-lg border border-white/10 bg-white/[0.02] flex items-center justify-center text-zinc-400 hover:text-white hover:border-emerald-500/30 transition-colors duration-200">
              {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
            </button>
            <NotificationsBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="profile-menu-button" className="flex items-center gap-2.5 rounded-lg border border-white/10 bg-white/[0.02] pl-1.5 pr-3 py-1.5 hover:border-emerald-500/30 transition-colors duration-200">
                  <HeaderAvatar user={user} />
                  <span className="hidden sm:block text-left leading-tight">
                    <span className="block text-xs font-semibold text-white">{user?.name}</span>
                    <span className="block text-[9px] uppercase tracking-[0.18em] text-zinc-500">{user?.role}</span>
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 border-white/10 bg-[#0A110D]/95 backdrop-blur-xl">
                <DropdownMenuLabel>
                  <p className="text-sm font-semibold text-white">{user?.name}</p>
                  <p className="text-xs text-zinc-500 font-normal">{user?.email}</p>
                  {user?.username && (
                    <p className="tnum text-[10px] text-zinc-600 font-normal mt-0.5" data-testid="menu-username">Login ID: {user.username}</p>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/[0.06]" />
                {user?.role === "investor" && (
                  <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile" className="text-zinc-300 focus:bg-white/[0.05] focus:text-white">
                    <User size={15} className="mr-2" /> My Profile
                  </DropdownMenuItem>
                )}
                {(user?.role === "admin" || user?.role === "wrm") && (
                  <DropdownMenuItem onClick={() => setAccountOpen(true)} data-testid="menu-account-settings" className="text-zinc-300 focus:bg-white/[0.05] focus:text-white">
                    <GearSix size={15} className="mr-2" /> Account Settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={doLogout} data-testid="logout-button" className="text-red-400 focus:bg-red-500/10 focus:text-red-300">
                  <SignOut size={15} className="mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-8 w-full max-w-[1500px] mx-auto">
          {subtitle && <p className="text-sm text-zinc-500 mb-6 -mt-2">{subtitle}</p>}
          {children}
        </main>
      </div>
      <AccountSettingsDialog open={accountOpen} onClose={() => setAccountOpen(false)} />
    </div>
  );
}

export function PageLoader() {
  return (
    <div className="space-y-4 fade-up" data-testid="page-loader">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <div key={i} className="skeleton h-24" />)}
      </div>
      <div className="skeleton h-72" />
    </div>
  );
}

export function EmptyState({ message }) {
  return (
    <div className="border border-dashed border-white/10 rounded-xl py-14 text-center" data-testid="empty-state">
      <p className="text-sm text-zinc-500">{message}</p>
    </div>
  );
}

export const thCls = "px-5 py-3 font-semibold text-left text-[10px] uppercase tracking-[0.14em] text-zinc-500";
export const tdCls = "px-5 py-3.5 border-b border-white/[0.04]";
export const rowCls = "hover:bg-white/[0.02] transition-colors duration-150";
